import {
    Autocomplete,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    TextField,
    useTheme,
} from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { Colleges } from '__generated__/graphql'
import IntakeCard from 'candidate/components/IntakeCard'
import { ChoiceBucket } from 'candidate/models/intake'
import React, { useEffect, useState } from 'react'
import NextButton from 'shared/components/NextButton'
import SecondaryButton from 'shared/components/SecondaryButton'
import TertiaryButton from 'shared/components/TertiaryButton'
import { getDropdownMaxHeight } from 'shared/style/constants'
import { getPxAsRem } from 'shared/utils'
import { educationIntakeYears } from 'shared/utils/intakeQuestionTools'
import { AdditionalButtonsContainer } from '../styles'

//TODO look to sync these answer types with BE
export interface UndergradCollegeAnswer {
    id: number
    college: ChoiceBucket | undefined
    majors: ChoiceBucket[] | undefined
    gpa: ChoiceBucket | undefined
    graduationDate?: string | null
    gpaScale: string
    error?: string
}
interface Props {
    answer?: UndergradCollegeAnswer[]
    callback: React.Dispatch<any>
    collegeChoices: Partial<Colleges>[]
    majorChoices: ChoiceBucket[]
    gpaChoices: ChoiceBucket[]
    gpaScales: string[]
    prompt: string
    skipCallback?: React.Dispatch<React.SetStateAction<string>>
    skipText?: string
}

const UndergradCollegeQuestion: React.FC<Props> = ({
    answer,
    callback,
    collegeChoices,
    majorChoices,
    gpaChoices,
    gpaScales,
    prompt,
    skipCallback,
    skipText,
}: Props) => {
    const [entries, setEntries] = useState<UndergradCollegeAnswer[]>([
        {
            id: 1,
            error: '',
            college: undefined,
            majors: undefined,
            gpa: undefined,
            graduationDate: undefined,
            gpaScale: '',
        },
    ])

    // Initialize entries with the answer if it exists
    useEffect(() => {
        if (answer && answer.length > 0) {
            const allAnswers: UndergradCollegeAnswer[] = []
            answer.map((entry, index) => {
                const ans: UndergradCollegeAnswer = {
                    id: index,
                    college: entry.college,
                    majors: entry.majors,
                    gpa: entry.gpa,
                    gpaScale: entry.gpaScale,
                    error: '',
                    graduationDate: entry.graduationDate,
                }
                allAnswers.push(ans)
                return ans
            })
            setEntries(allAnswers)
        }
    }, [answer])

    // Add a new entry
    const addEntry = () => {
        setEntries([
            ...entries,
            {
                id: entries.length + 1,
                error: '',
                college: undefined,
                majors: undefined,
                gpa: undefined,
                gpaScale: '',
                graduationDate: undefined,
            },
        ])
    }

    // Remove the last entry
    const removeEntry = () => {
        if (entries.length > 1) {
            setEntries(entries.slice(0, -1))
        }
    }

    // Update the state for a specific entry
    const updateEntry = (
        id: number,
        updatedEntry: Partial<UndergradCollegeAnswer>
    ) => {
        setEntries(
            entries.map((entry) => {
                if (entry.id === id) {
                    const updated = { ...entry, ...updatedEntry }
                    if (
                        updated.gpaScale &&
                        updated.gpa &&
                        parseFloat(updated.gpaScale) <
                            parseFloat(updated.gpa.choice)
                    ) {
                        updated.error =
                            'GPA cannot be higher than the GPA scale'
                    } else {
                        updated.error = ''
                    }
                    return updated
                }
                return entry
            })
        )
    }

    const submitData = () => {
        const response = entries
        callback({ response })
    }

    const years = educationIntakeYears()
    const theme = useTheme()
    const dropdownMaxHeight = getDropdownMaxHeight(theme)

    return (
        <IntakeCard
            question={prompt}
            skipCallback={skipCallback}
            skipText={skipText}
        >
            {entries.map((entry, index) => (
                <Grid
                    container
                    style={{
                        display: 'inline-block',
                        marginBottom: getPxAsRem(36),
                        marginTop: getPxAsRem(16),
                    }}
                    key={entry.id}
                >
                    <Grid item xs={12} sm={6}>
                        <FormControl
                            style={{
                                marginBottom: getPxAsRem(24),
                            }}
                            fullWidth
                        >
                            <Autocomplete
                                key={`${entry.id}_majors`}
                                multiple
                                id="majors"
                                isOptionEqualToValue={(option, value) =>
                                    option.choice === value.choice
                                }
                                onChange={(event, newValue) => {
                                    updateEntry(entry.id, {
                                        majors: newValue,
                                    })
                                }}
                                options={majorChoices}
                                getOptionLabel={(option) => option.choice}
                                value={entry?.majors || []}
                                defaultValue={entry.majors || []}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Majors"
                                        placeholder="Majors"
                                        inputProps={{
                                            ...params.inputProps,
                                        }}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl
                            style={{
                                marginBottom: getPxAsRem(24),
                            }}
                            fullWidth
                        >
                            <InputLabel id="college_label">
                                College/University
                            </InputLabel>
                            <Select
                                id="college"
                                labelId="college_label"
                                label="College/University"
                                value={entry.college?.choice || ''}
                                onChange={(e) => {
                                    const collegeSelection =
                                        collegeChoices.find(
                                            (college) =>
                                                college.value === e.target.value
                                        )
                                    updateEntry(entry.id, {
                                        college: collegeSelection
                                            ? {
                                                  choice: collegeSelection.value!,
                                              }
                                            : undefined,
                                    })
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: dropdownMaxHeight,
                                        },
                                    },
                                }}
                            >
                                {collegeChoices.map((college) => (
                                    <MenuItem
                                        key={college.id}
                                        value={college.value}
                                    >
                                        {college.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        {/* Graduation Date Picker */}
                        <FormControl
                            style={{
                                marginBottom: getPxAsRem(24),
                            }}
                            fullWidth
                        >
                            <InputLabel id="graduation-date-label">
                                Graduation Date
                            </InputLabel>
                            <Select
                                id="graduation-date"
                                labelId="graduation-date-label"
                                label="Graduation Date"
                                value={entry.graduationDate || ''}
                                onChange={(e) => {
                                    updateEntry(entry.id, {
                                        graduationDate: e.target.value,
                                    })
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: dropdownMaxHeight,
                                        },
                                    },
                                }}
                            >
                                {years.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid container>
                        <Grid
                            item
                            xs={4}
                            sm={2}
                            sx={{
                                pr: getPxAsRem(16),
                            }}
                        >
                            <FormControl fullWidth error={Boolean(entry.error)}>
                                <InputLabel id="gpa_label">
                                    Cumulative GPA
                                </InputLabel>
                                <Select
                                    id="gpa"
                                    labelId="gpa_label"
                                    label="Cumulative GPA"
                                    value={entry.gpa?.choice || ''}
                                    onChange={(
                                        e: SelectChangeEvent<string>
                                    ) => {
                                        const gpaSelection = gpaChoices.find(
                                            (choice: ChoiceBucket) =>
                                                choice.choice === e.target.value
                                        )
                                        updateEntry(entry.id, {
                                            gpa: gpaSelection,
                                        })
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: dropdownMaxHeight,
                                            },
                                        },
                                    }}
                                >
                                    {gpaChoices.map((choice: ChoiceBucket) => (
                                        <MenuItem
                                            key={choice.choice}
                                            value={choice.choice}
                                        >
                                            {choice.choice}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {entry.error && (
                                    <FormHelperText>
                                        GPA cannot be higher than the GPA scale
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>

                        <Grid item xs={8} sm={4}>
                            <FormControl
                                style={{
                                    marginBottom: getPxAsRem(24),
                                }}
                                fullWidth
                            >
                                <InputLabel id="gpa-scale">
                                    GPA Scale
                                </InputLabel>
                                <Select
                                    id="gpa-scale"
                                    label="GPA Scale"
                                    labelId="gpa-scale_label"
                                    value={entry.gpaScale?.toString() || ''}
                                    onChange={(
                                        e: SelectChangeEvent<string>
                                    ) => {
                                        const selectedGpaScale = e.target.value

                                        updateEntry(entry.id, {
                                            gpaScale: selectedGpaScale,
                                        })
                                    }}
                                >
                                    {gpaScales.map((scale: string) => (
                                        <MenuItem key={scale} value={scale}>
                                            {scale}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {entry.error && (
                                    <FormHelperText>
                                        GPA cannot be higher than the GPA scale
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
            ))}
            <AdditionalButtonsContainer>
                {entries.length > 1 && (
                    <TertiaryButton
                        disabled={entries.length <= 1}
                        onClickHandler={removeEntry}
                        title={'Remove Undergraduate Degree'}
                        style={{ marginRight: '1rem' }}
                    />
                )}
                <SecondaryButton
                    title="Add Additional Undergraduate Degree"
                    disabled={false}
                    onClickHandler={addEntry}
                />
            </AdditionalButtonsContainer>
            <NextButton
                disabled={entries.some(
                    (entry) =>
                        !entry.college ||
                        !entry.majors?.length ||
                        !entry.gpa ||
                        !entry.gpaScale ||
                        !entry.graduationDate ||
                        Boolean(entry.error)
                )}
                onClickHandler={submitData}
            />
        </IntakeCard>
    )
}

export default UndergradCollegeQuestion
